// @flow

import React, { Fragment } from 'react';
import type { Node } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector, shallowEqual } from 'react-redux';
import { Button } from '@riseart/common';
import { application as CONFIG_APP } from 'Config';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { useLocale } from 'shared_services/redux/hooks/useLocale';
import { selectUnitSystem } from 'shared_services/redux/selectors/unitSystem';
import { formatCurrency } from 'shared_services/riseart/utils/Utils';
import { formatDimensionsLabel } from 'shared_services/riseart/utils/artDataUtils';
import { ParallaxBanner } from 'shared_components/common/banner/Parallax';

import { cmsParallaxContentCls } from 'shared_components/cms/modules/banner/Parallax.less';

type Props = {
  align?: 'left' | 'center' | 'right',
  art?: Object,
  media: Object,
  caption?: string,
  content?: Object,
};

/**
 * CmsModuleParallaxBanner
 *
 * @param {Props} props
 * @returns {Node}
 */
export function CmsModuleParallaxBanner({ art, caption, ...restProps }: Props): Node {
  const locale = useLocale();
  const { unitSystem } = useSelector(selectUnitSystem, shallowEqual);
  const lengthUnit = CONFIG_APP.units[unitSystem].length;

  const captionContent = art ? (
    <Fragment>
      <div className={cmsParallaxContentCls}>
        <div>
          <strong>{art.title}</strong>
        </div>
        {art.artistName ? <div>{art.artistName.toUpperCase()}</div> : null}
        {art.medium ? (
          <Fragment>
            <FormattedMessage id={`filters.art.medium.${art.medium}`} /> -
          </Fragment>
        ) : null}
        {formatDimensionsLabel([art.height, art.width, art.depth], art.unit, {
          convertTo: lengthUnit.unit,
          precision: 0,
        })}
        <div>
          {formatCurrency(art.skuBuyPrice, art.storeCode, undefined, {
            locale: locale && locale.name,
            hasSpaceBetweenCurrency: true,
            precision: 0,
          })}
        </div>
      </div>
      <Button type="ghost" size="small" isAnchor to={UrlAssembler.artDetail(art.id, art.slug)}>
        <FormattedMessage id="common.moreInfo">{(label) => label}</FormattedMessage>
      </Button>
    </Fragment>
  ) : (
    caption || null
  );

  return <ParallaxBanner {...restProps} caption={captionContent} />;
}
